// src/components/Shared/BigButton.jsx
// Large, touch-friendly button with loading states

import React from 'react';

const BigButton = ({
	children,
	onClick,
	variant = 'primary',
	size = 'large',
	loading = false,
	disabled = false,
	fullWidth = false,
	icon,
	className = '',
	type = 'button',
	...props
}) => {
	const variants = {
		primary: 'btn-primary',
		secondary: 'btn-secondary',
		success: 'btn-success',
		danger: 'btn-danger',
		warning: 'btn-warning',
		info: 'btn-info',
		light: 'btn-light',
		dark: 'btn-dark',
		outline: 'btn-outline-primary',
		// Brand variants for guest-facing surfaces — follow the event's
		// branding color (--ev-brand-color) and fall back to Eventory purple.
		brand: 'ev-btn-brand',
		brandOutline: 'ev-btn-brand-outline',
	};

	const sizes = {
		small: { padding: '8px 16px', fontSize: '14px', minHeight: '40px' },
		medium: { padding: '12px 24px', fontSize: '16px', minHeight: '48px' },
		large: { padding: '16px 32px', fontSize: '18px', minHeight: '56px' },
		xlarge: { padding: '20px 40px', fontSize: '20px', minHeight: '64px' },
	};

	const sizeStyle = sizes[size] || sizes.large;

	const style = {
		...sizeStyle,
		minWidth: '120px',
		width: fullWidth ? '100%' : 'auto',
		fontWeight: '600',
		borderRadius: '8px',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '8px',
		cursor: disabled || loading ? 'not-allowed' : 'pointer',
		opacity: disabled ? 0.6 : 1,
		transition: 'all 0.2s ease',
		touchAction: 'manipulation', // Prevents zoom on double-tap
	};

	return (
		<button
			type={type}
			className={`btn ${variants[variant] || variants.primary} ${className}`}
			style={style}
			onClick={onClick}
			disabled={disabled || loading}
			{...props}
		>
			{loading ? (
				<>
					<span 
						className="spinner-border spinner-border-sm" 
						role="status" 
						aria-hidden="true"
					/>
					<span>Loading...</span>
				</>
			) : (
				<>
					{icon && <span className="button-icon">{icon}</span>}
					{children}
				</>
			)}
		</button>
	);
};

export default BigButton;

